import { useState } from 'react';
import { GlobeIcon } from 'lucide-react';
import { Button, Card, Input } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import { updateLandingPageDomainSchema } from '@tinct/core-api-models';

export type RegisterDomainCardProps = {
  className?: string;
  /** Server-side rejection message (400 invalid domain / 409 already taken). */
  errorMessage?: string | null;
  isSaving?: boolean;
  onSave: (domain: string) => void;
};

/**
 * Empty state: no custom domain registered yet. Explains the two hosting
 * modes and offers the single input that registers one for the workspace.
 */
export function RegisterDomainCard({
  className,
  errorMessage,
  isSaving,
  onSave,
}: RegisterDomainCardProps) {
  const [domain, setDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = () => {
    const parsed = updateLandingPageDomainSchema.safeParse({
      domain: domain.trim().toLowerCase(),
    });
    if (!parsed.success) {
      setValidationError(
        parsed.error.issues[0]?.message ?? 'Invalid domain',
      );
      return;
    }
    setValidationError(null);
    onSave(parsed.data.domain);
  };

  const displayedError = validationError ?? errorMessage;

  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex flex-col gap-1.5 px-6 pt-6">
        <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
          Use your own domain
        </h3>
        <p className="text-sm text-muted-foreground">
          Your landing pages are always live on their tinct URL. Add your own
          domain to serve them all under it, at{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">
            your-domain.com/&#123;page&#125;
          </code>
          . One CNAME record, one certificate — every published page, past and
          future, is served there automatically.
        </p>
      </div>

      <div className="px-6 py-6">
        <div className="flex items-center gap-6">
          <span className="text-base font-medium text-foreground">Domain</span>
          <div className="flex flex-1 flex-col gap-1">
            <div className="relative">
              <GlobeIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder="lp.your-domain.com"
                className="pl-9"
              />
            </div>
            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end border-t px-6 py-6">
        <Button onClick={handleSave} disabled={!domain.trim()} isLoading={isSaving}>
          Save
        </Button>
      </div>
    </Card>
  );
}
